import { getApiUrl } from '../config/api';

// A single shared, lazily-populated directory of {id/username/name -> profilePicture}
// so every place in the app that shows a user's name can also show their photo
// without each component having to fetch /api/users itself (N+1 requests) or
// thread a users list through props everywhere.
interface DirectoryEntry {
  id: string;
  profilePicture: string;
  name: string;
}

const byId = new Map<string, DirectoryEntry>();
const byName = new Map<string, DirectoryEntry>();
// Names seen more than once — e.g. two different people both named "Karthik S".
// byId is always unambiguous (real user IDs are unique), but the byName
// fallback is only used when a caller has no ID to look up by. Without this
// guard, the second user loaded here would silently overwrite the first in
// byName, and either person's avatar could end up showing the *other*
// person's uploaded photo. Once a name is known to be shared, lookups by
// that name are refused so the caller falls back to initials instead.
const ambiguousNames = new Set<string>();
let loaded = false;
let loadingPromise: Promise<void> | null = null;
const listeners = new Set<() => void>();

async function load() {
  try {
    const response = await fetch(getApiUrl('api/users'));
    if (!response.ok) return;
    const users = await response.json();
    users.forEach((u: any) => {
      if (!u.id) return;
      const entry: DirectoryEntry = { id: u.id, profilePicture: u.profilePicture || '', name: u.name || u.username || '' };
      byId.set(u.id, entry);
      [u.username, u.name].forEach((raw) => {
        if (!raw) return;
        const key = raw.toLowerCase();
        const existing = byName.get(key);
        if (existing && existing.id !== u.id) {
          ambiguousNames.add(key);
          byName.delete(key);
        } else if (!ambiguousNames.has(key)) {
          byName.set(key, entry);
        }
      });
    });
  } catch (err) {
    console.error('Error loading user directory:', err);
  } finally {
    loaded = true;
    listeners.forEach(fn => fn());
  }
}

export function ensureUserDirectoryLoaded() {
  if (!loadingPromise) {
    loadingPromise = load();
  }
  return loadingPromise;
}

export function isUserDirectoryLoaded() {
  return loaded;
}

export function lookupProfilePicture(idOrName?: string): string {
  if (!idOrName) return '';
  return byId.get(idOrName)?.profilePicture || byName.get(idOrName.toLowerCase())?.profilePicture || '';
}

export function subscribeUserDirectory(fn: () => void) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
